/**
 * AbstractContent module
 *
 * This class is extended by Content and ContentSet class
 *
 */

/* eslint no-prototype-builtins: "off" */

'use strict';

const _ = require('lodash');

const Mediator = require('./../../../core/mediator');

const uniqId = require('uniq-id');
const jQuery = require('jquery');
const Revision = require('./Revision');
const ContentRepository = require('./../repository/ContentRepository');

/**
 *
 * AbstractContent class is an abstract
 * for Content and ContentSet class
 *
 * A content is linked to an revision for keep a state with a old version
 *
 *  @author Florian Kroockmann <florian.kroockmann@lp-digital.fr>
 */
class AbstractContent {
  /**
   * Constructor initialize the object with a config which contain:
   *
   * Mandatory:
   *   - uid (Identifier of content)
   *   - type (Nature of content)
   *   - definition (Informations of the content)
   *
   * Optionnal:
   *   - $ (jQuery object)
   *
   * All data in config is set into the data object
   *
   * An uniq id is set to jQuery object if exist
   *
   * @param {object} config
   */
  constructor(config) {
    this.uid = null;
    this.type = null;
    this.id = '_' + (uniqId().substring(0, 5));
    this.updated = false;
    this.$ = null;
    this.definition = null;
    this.revision = null;
    this.data = null;

    this.populateConfig(config);

    this.setId();

    this.revision = new Revision({'uid': this.uid, 'type': this.type});
  }

  /**
   * Return the rte config from definition if the content is rte editable
   *
   * @returns {object} rte config
   */
  getRteConfig() {
      return this.isRteEditable() ? this.definition.properties.rte : {};
  }

  /**
   * Return the forbidden plugins for a content
   *
   * If the element have a cloud-placeholder we force to use click. This way
   *
   * If jquery element have an forbidden-plugins data, we use it at first else
   * that retrieved from a definition
   *
   * @returns {object} forbidden plugins
   */
  getForbiddenPlugins() {
    let forbiddenPlugins = this.definition.properties.forbidden_plugins || [];

    if (this.$ instanceof jQuery) {
      let data = this.$.data('forbidden-plugins');

      if (data) {
        forbiddenPlugins = data.split(',');
      }

      if (this.$.find('.cloud-placeholder').length) {
        if (-1 !== forbiddenPlugins.indexOf('click')) {
          _.pull(forbiddenPlugins, 'click');
        }
      }
    }

    return forbiddenPlugins;
  }

  /**
   * Return the colContentSet of the content with the jQuery object
   *
   * ContentManager is used for build the element from the jQuery object
   *
   * @returns {object | null} colContentSet
   */
  getColContentSet() {
    let colContentSet = null;

    if (this.$ instanceof jQuery) {
      let ContentManager = require('./../component/ContentManager');
      let $parent = this.$.parents('.colcontentset:first');

      if ($parent.length) {
        colContentSet = ContentManager.getContentByNode($parent);
      }
    }

    return colContentSet;
  }

  /**
   * Return the cloudContentSet of the content with the jQuery object
   *
   * ContentManager is used for build the element from the jQuery object
   *
   * @returns {object | null} cloudContentSet
   */
  getCloudContentSet() {
    let colContentSet = null;

    if (this.$ instanceof jQuery) {
      let ContentManager = require('./../component/ContentManager');
      let $parent = this.$.parents('.cloudcontentset:first');

      if ($parent.length) {
        colContentSet = ContentManager.getContentByNode($parent);
      }
    }

    return colContentSet;
  }

  /**
   * Return the definition of the content
   *
   * If an key is set, a sub element of the definition is retrieved if exist
   *
   * @param {string} key
   * @returns {Mixed} definition
   */
  getDefinition(key) {
    let res = null;

    if (_.isString(key)) {
      if (this.definition.hasOwnProperty(key)) {
        res = this.definition[key];
      }
    } else {
      res = this.definition;
    }

    return res;
  }

  /**
   * Return the properties of the content
   *
   * If an key is set, a sub element of the properties is retrieved if exist
   *
   * @param {string} key
   * @returns {Mixed} properties
   */
  getProperties(key) {
    let res = null;

    if (_.isString(key)) {
      if (this.definition.properties.hasOwnProperty(key)) {
        res = this.definition.properties[key];
      }
    } else {
      res = this.definition.properties;
    }

    return res;
  }

  /**
   * Return the label from definition or the type
   *
   * @returns {string} label
   */
  getLabel() {
    return this.definition.properties.name || this.type;
  }

  /**
   * Return the rendermode of content, default is returned if not exist
   *
   * @returns {string} rendermode
   */
  getRendermode() {
      return (this.$ instanceof jQuery) ? this.$.data('rendermode') || 'default' : 'default';
  }

  /**
   * Return the element from the revision if is a new or updated element is set
   * else from the data
   *
   * @throws {Error} Key must be set
   * @param {string} key
   * @returns {object} element
   */
  getElement(key) {
      if (!_.isString(key)) {
          throw new Error('Missing key argument on getElement function');
      }

      return this.revision.getElement(key) || (this.data ? this.data.elements[key] : undefined);
  }

  /**
   * Return the accept of a content from the jQuery object as first and from the
   * definition as second
   *
   * @returns {object} accept
   */
  getAccept() {
      let accept = this.getDefinition('accept');

      if (this.$ instanceof jQuery) {
          accept = this.$.data('accept') ? this.$.data('accept').split(',') : accept;
      }

      return accept;
  }

  /**
   * Return the content parent with the jQuery object
   *
   * @returns {Mixed} parent
   */
  getParent() {
    if (this.$ instanceof jQuery) {
      let ContentManager = require('./../component/ContentManager');
      let $parent = this.$.parents('.bb-content:first');

      if (!$parent.length) {
        return null;
      }

      return ContentManager.getContentByNode($parent);
    }

    return null;
  }

  /**
   * Return the parameter from the revision is a new or updated parameter is set
   * else from the data
   *
   * @throws {Error} Key must be set
   * @param {string} key
   * @returns {object | undefined} parameter
   */
  getParameter(key) {
    if (!_.isString(key)) {
      throw new Error('Missing key argument on getParameter function');
    }

    return this.revision.getParameter(key) || (this.data ? this.data.parameters[key] : undefined);
  }

  /**
   * Return the data from the api
   *
   * If key is set a sub data is retrieved with this key if exist
   *
   * The force parameter prevent a new call from api if false
   *
   * @param {string} key
   * @param {boolean} force
   *
   * @returns {Promise} data
   */
  getData(key, force) {
    let dfd = jQuery.Deferred();
    let callback = function(data, key) {
          return key ? data[key] : data;
        };

    if (!this.data || true === force) {
      ContentRepository.findData(this.type, this.uid).done((data) => {
        this.data = data;

        dfd.resolve(callback(data, key));
      }).fail(function(data, response) {
        dfd.reject(data, response);
      });
    } else {
      dfd.resolve(callback(this.data, key));
    }

    return dfd.promise();
  }

  /**
   * Return the information of a content from the api
   *
   * @returns {Promise} information
   */
  getInfo() {
    let dfd = jQuery.Deferred();

    if (!this.info) {
      ContentRepository.findInfo(this.type, this.uid).done((info) => {
        this.info = info;
        dfd.resolve(info);
      }).fail(function(data, response) {
        dfd.reject(data, response);
      });
    } else {
      dfd.resolve(this.info);
    }

    return dfd.promise();
  }

  /**
   * Return the html of the content from the api
   *
   * Replace prevent to change the $ attribute of this
   *
   * @param {boolean} replace
   * @returns {Promise} html
   */
  getHtml(replace) {
      let dfd = jQuery.Deferred();

      ContentRepository.getHTML(this.type, this.uid, this.getRendermode()).done((html) => {
        html = jQuery(html);

        if (replace !== false) {
          this.$ = html;
        }

        dfd.resolve(html);
      });

      return dfd.promise();
  }

  /**
   * Set an uniq id to the jQuery object
   *
   * @returns {AbstractContent} this
   */
  setId() {
    if (this.$ instanceof jQuery) {
      this.$.attr('data-bb-id', this.id);
    }

    return this;
  }

  /**
   * Set the updated state to the content
   *
   * @param {boolean} isUpdate
   * @returns {AbstractContent} this
   */
  setUpdated(isUpdate) {
    this.updated = Boolean(isUpdate);

    return this;
  }

  /**
   * Set elements to the revision and change the updated state
   *
   * @param {object} elements
   * @returns {AbstractContent} this
   */
  setElements(elements) {
    this.revision.setElements(elements);
    this.setUpdated(true);

    return this;
  }

  /**
   * Set parameters to the revision and change the updated state
   *
   * @param {object} parameters
   * @returns {AbstractContent} this
   */
  setParameters(parameters) {
    this.revision.setParameters(parameters);
    this.setUpdated(true);

    return this;
  }

  /**
   * Return if the content is rte editable from the definition
   *
   * @returns {boolean} rte editable
   */
  isRteEditable() {
    return Boolean(this.definition.properties.rte);
  }

  /**
   * Return if the content is editable from jQuery object
   *
   * @returns {boolean} editable
   */
  isEditable() {
    if (this.$ instanceof jQuery) {
      return (undefined === this.$.data('editable')) ? true : Boolean(this.$.data('editable'));
    }

    return true;
  }

  /**
   * Return if the content is an contentset
   *
   * @returns {boolean} is contentset
   */
  isContentSet() {
    return this.definition.properties.is_container;
  }

  /**
   * An element is savable if the revision is not empty
   *
   * @returns {boolean} savable
   */
  isSavable() {
    return !this.revision.isEmpty();
  }

  /**
   * Add element to the revision and set updated state
   *
   * @throws {Error} key must be set
   * @param {string} key
   * @param {Mixed} value
   * @returns {AbstractContent} this
   */
  addElement(key, value) {
    if (!_.isString(key)) {
      throw new Error('Missing key argument on addElement function');
    }

    this.revision.addElement(key, value);
    this.setUpdated(true);

    return this;
  }

  /**
   * Add parameter to the revision and set updated state
   *
   * @throws {Error} key must be set
   * @param {string} key
   * @param {Mixed} value
   * @returns {AbstractContent} this
   */
  addParameter(key, value) {
    if (!key) {
      throw new Error('Missing key argument on addParameter function');
    }

    this.revision.addParameter(key, value);
    this.setUpdated(true);

    return this;
  }

  /**
   * add class to the jquery object if exist
   *
   * @param {string} className
   * @returns {AbstractContent} this
   */
  addClass(className = '') {
    if (this.$ instanceof jQuery) {
      this.$.addClass(className);
    }

    return this;
  }

  /**
   * remove class to the jquery object if exist
   *
   * @param {string} className
   * @returns {AbstractContent} this
   */
  removeClass(className) {
    if (this.$ instanceof jQuery && _.isString(className)) {
      this.$.removeClass(className);
    }

    return this;
  }

  /**
   * refresh the content and replace the $ of this
   *
   * @event after:content:refresh this
   * @returns {Promise} html
   */
  refresh() {
    let dfd = jQuery.Deferred();

    if (this.$ instanceof jQuery) {
      let parent = this.getParent();
      if (null !== parent && !parent.isContentSet()) {
        parent.refresh().done(() => {
          require('./../component/ContentManager').refreshjQueryObject(this);

          dfd.resolve();
        });
      } else {
        this.getHtml(false).done((html) => {
          html = jQuery(html);

          this.$.replaceWith(html);

          this.$ = html;

          Mediator.publish('after:content:refresh', this);

          dfd.resolve();
        }).fail(function(...args) {
          dfd.reject(args);
        });
      }
    } else {
      dfd.resolve();
    }

    return dfd.promise();
  }

  /**
   * Populate the config from the constructor and set all unknown data to
   * the content data object
   *
   * @param {object} config
   * @returns {AbstractContent} this
   */
  populateConfig(config) {
    if (!_.isObject(config)) {
        throw new Error('Config must be set');
    }

    _.forEach(config, (value, key) => {
        this[key] = value;
    });

    if (null === this.uid) {
        throw new Error('Uid must be set');
    }

    if (null === this.type) {
        throw new Error('Type must be set');
    }

    if (null === this.definition) {
        throw new Error('Definition must be set');
    }

    return this;
  }
}

module.exports = AbstractContent;
